import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB per chunk (Cloudflare compatible)
const CHUNK_TIMEOUT = 30000; // 30s per chunk
const ASSEMBLY_TIMEOUT = 60000; // 60s for last chunk (assembly only, cloud is async)
const MAX_RETRIES = 3;

async function uploadChunkWithRetry(url, formData, config, retries = MAX_RETRIES) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await axios.post(url, formData, config);
    } catch (err) {
      const status = err.response?.status;
      // Retry on 520 (Cloudflare), 502, 503, 504, or network errors
      if (attempt < retries && (!status || status >= 500)) {
        await new Promise(r => setTimeout(r, 1000 * attempt)); // wait 1s, 2s, 3s
        continue;
      }
      throw err;
    }
  }
}

/**
 * Upload a file - uses chunked upload for files > 1MB to bypass proxy/Cloudflare limits
 */
export async function uploadFile(file, token, onProgress) {
  const headers = { Authorization: `Bearer ${token}` };

  if (file.size <= CHUNK_SIZE) {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await uploadChunkWithRetry(`${API}/files/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded / e.total) * 100)) : undefined,
      });
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al subir archivo';
      throw new Error(msg);
    }
  }

  // Large file: chunked upload with retry
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append('file', chunk, `chunk_${i}`);
    formData.append('upload_id', uploadId);
    formData.append('chunk_index', i.toString());
    formData.append('total_chunks', totalChunks.toString());
    formData.append('original_name', file.name);

    try {
      const isLastChunk = (i === totalChunks - 1);
      const res = await uploadChunkWithRetry(`${API}/files/upload-chunk`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: isLastChunk ? ASSEMBLY_TIMEOUT : CHUNK_TIMEOUT,
      });

      if (onProgress) {
        onProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      if (res.data.complete) {
        return res.data;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || `Error al subir fragmento ${i + 1}/${totalChunks}`;
      throw new Error(msg);
    }
  }

  throw new Error('Error: el archivo no se ensambló correctamente');
}


/**
 * Friendly mapping from backend `reason` codes to user-facing Spanish messages.
 */
export function reasonToMessage(reason) {
  switch (reason) {
    case 'not_indexed':
      return 'Este archivo no está registrado en el sistema. Avisa al profesor para que lo vuelva a subir.';
    case 'deleted':
      return 'Este archivo fue eliminado.';
    case 'broken_link':
      return 'El enlace físico del archivo está roto. Avisa al administrador para restaurarlo.';
    case 'local_missing':
      return 'Este archivo se perdió en la última actualización del sistema. Avisa al profesor para que lo vuelva a subir.';
    case 'cloud_unavailable':
      return 'Almacenamiento en la nube temporalmente no disponible. Inténtalo en unos minutos.';
    case 'unknown_state':
      return 'No se pudo verificar el estado del archivo. Inténtalo de nuevo más tarde.';
    default:
      return 'Este archivo no está disponible temporalmente.';
  }
}

/**
 * Pre-flight check for a file. Returns {available, reason, original_name, warning?}.
 * Never throws on 4xx — returns a structured response the UI can render.
 */
export async function checkFileAvailable(filename, token) {
  try {
    const res = await fetch(`${API}/files/check/${encodeURIComponent(filename)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      return { available: false, reason: 'unknown_state', original_name: filename };
    }
    return await res.json();
  } catch (err) {
    return { available: false, reason: 'unknown_state', original_name: filename };
  }
}

/**
 * Download a file as a Blob and trigger browser download without opening a new tab.
 * Works for PDF, DOC and any document type.
 *
 * UI Validation Layer: pre-checks availability and throws a user-friendly error
 * (in Spanish) when the file is unavailable instead of letting a raw 404 bubble up.
 */
export async function downloadBlob(filename, originalName, token) {
  // Pre-flight integrity check (validation layer)
  const status = await checkFileAvailable(filename, token);
  if (!status.available) {
    throw new Error(reasonToMessage(status.reason));
  }

  const url = `${API}/files/${filename}`;
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(reasonToMessage('local_missing'));
    }
    throw new Error(`Error al descargar (${response.status}). Inténtalo más tarde.`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = originalName || filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after short delay to let download start
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
